import { inject } from 'mobx-react';
import * as React from 'react';

import { AbacusControl, AbacusExperiment, AbacusVariant } from 'bernie-abacus';
import { ErrorBoundary } from 'bernie-view';
import { LocalizedComponentProps, withLocalization } from 'bernie-l10n';
import { ViewExtraLarge, ViewLarge, ViewMedium, Viewport, ViewSmall } from 'bernie-view';

import { AdsenseContainer, DisplayAd } from 'constants/ads';
import { DisplayAdModel } from 'models/common/ads-types';
import { ExtendedContextStore } from 'types/flex-definitions';
import { PageName } from 'constants/pages';

import { MesoAdsenseCsa, MesoCsaSlot } from './meso-adsense';
import { MesoDisplayAd } from './meso-display-ad';
import { MesoTrigger } from './meso-trigger';
import { SwxlFooter } from 'components/common/footer/swxl-footer';

interface MesoFooterProps {
  destination: string;
  pageName: string;
  isDateless: boolean;
  context?: ExtendedContextStore;
}

const breakpointsFrameworkExperiment = 'MeSo_PWA_Breakpoints_Framework';

@withLocalization
@inject('context')
export class MesoFooter extends React.Component<MesoFooterProps & LocalizedComponentProps> {
  public render() {
    const { l10n, pageName, destination, isDateless, context } = this.props;
    const searchQuery = l10n.formatText('meso.adsense.query', destination);

    return pageName === PageName.SEARCH ? (
      <HSRFooter searchQuery={searchQuery} context={context} />
    ) : (
      <HISFooter searchQuery={searchQuery} isDateless={isDateless} context={context} />
    );
  }
}

interface FooterProps {
  searchQuery: string;
  isDateless?: boolean;
  context?: ExtendedContextStore;
}

const showDisplayAdComponent = (pageName: PageName) => (ads: DisplayAdModel[]) => <DisplayAdComponent ads={ads} pageName={pageName} />;

const showDisplayAdComponentHSR = showDisplayAdComponent(PageName.SEARCH);

const HSRFooter: React.FunctionComponent<FooterProps> = ({ searchQuery }) => (
  <div className="uitk-grid all-grid-align-center all-t-padding-six">
    <AbacusExperiment name={breakpointsFrameworkExperiment}>
      <AbacusControl>
        <Viewport>
          <ViewMedium>{showDisplayAdComponentHSR([DisplayAd.HSR_FOOTER_M_CM2, DisplayAd.HSR_FOOTER_M_CM1])}</ViewMedium>
          <ViewLarge>{showDisplayAdComponentHSR([DisplayAd.HSR_FOOTER_L_CM2, DisplayAd.HSR_FOOTER_L_CM1])}</ViewLarge>
          <ViewExtraLarge>
            <>
              <div className="uitk-cell all-cell-1-1 all-b-padding-six">
                {showDisplayAdComponentHSR([DisplayAd.HSR_FOOTER_XL_CM2, DisplayAd.HSR_FOOTER_XL_CM1])}
              </div>
              <div className="uitk-cell all-cell-1-1">
                <AdSenseComponent container={AdsenseContainer.HSR_FOOTER} searchQuery={searchQuery} width="100%" />
              </div>
            </>
          </ViewExtraLarge>
        </Viewport>
      </AbacusControl>
      <AbacusVariant bucket={1}>
        <>
          <section className="uitk-grid all-grid-align-center">
            <meso-media-query uci="CM1">
              <div>
                <meso-display-ad class="ad-div display-ad" uci="CM1" />
              </div>
            </meso-media-query>
            <meso-media-query uci="CM2">
              <div>
                <meso-display-ad class="ad-div display-ad" uci="CM2" />
              </div>
            </meso-media-query>
          </section>
          <Viewport>
            <ViewExtraLarge>
              <div className="uitk-grid all-grid-align-center all-t-padding-six">
                <div className="uitk-cell all-cell-1-1">
                  <AdSenseComponent container={AdsenseContainer.HSR_FOOTER} searchQuery={searchQuery} width="100%" />
                </div>
              </div>
            </ViewExtraLarge>
          </Viewport>
        </>
      </AbacusVariant>
    </AbacusExperiment>
  </div>
);

const showDisplayAdComponentHIS = showDisplayAdComponent(PageName.INFOSITE);

const HISFooter: React.FunctionComponent<FooterProps> = ({ searchQuery, isDateless, context }) => (
  <AbacusExperiment name={breakpointsFrameworkExperiment}>
    <AbacusControl>
      <Viewport>
        <ViewSmall>
          <>
            <div className="uitk-grid all-grid-align-center all-y-padding-four all-x-padding-four">
              {isDateless && <SwxlFooter context={context} className="uitk-cell all-cell-1-1 all-b-padding-four swxl" />}
              <div className="uitk-cell all-cell-1-1 all-b-padding-four">
                <div className="uitk-grid all-grid-align-center">
                  <MesoDisplayAd uci={DisplayAd.HIS_FOOTER_S_CM6.uci} mediaquery={DisplayAd.HIS_FOOTER_S_CM6.mediaquery} />
                </div>
              </div>
              <MesoTrigger pageName={PageName.INFOSITE} />
              {isDateless && (
                <div className="uitk-cell all-cell-1-1">
                  <AdSenseComponent container={AdsenseContainer.HIS_FOOTER_1} searchQuery={searchQuery} width="100%" isMobile={true} />
                </div>
              )}
            </div>
          </>
        </ViewSmall>
        <ViewMedium>
          <>
            <div className="uitk-grid all-grid-align-center all-y-padding-four">
              {isDateless && <SwxlFooter context={context} className="uitk-cell all-cell-1-1 all-b-padding-four" />}
              <div className="uitk-cell all-cell-1-1 all-b-padding-four">
                {showDisplayAdComponentHIS([DisplayAd.HIS_FOOTER_M_CM3, DisplayAd.HIS_FOOTER_M_CM2])}
              </div>
              <div className="uitk-cell all-cell-1-1">
                <AdSenseComponent container={AdsenseContainer.HIS_FOOTER_1} searchQuery={searchQuery} width="468px" />
              </div>
            </div>
          </>
        </ViewMedium>
        <ViewLarge>
          <>
            <div className="uitk-grid all-grid-align-center all-y-padding-four all-cell-1-2">
              <div className="uitk-cell all-cell-1-1 all-b-padding-four">{showDisplayAdComponentHIS([DisplayAd.HIS_FOOTER_L_XL_CM1])}</div>
              {isDateless && <SwxlFooter context={context} className="uitk-cell all-r-padding-four uitk-flex-grow-1" />}
              <div className="uitk-cell uitk-flex-grow-1">
                <AdSenseComponent container={AdsenseContainer.HIS_FOOTER_2} searchQuery={searchQuery} width="100%" />
              </div>
            </div>
          </>
        </ViewLarge>
      </Viewport>
    </AbacusControl>
    <AbacusVariant bucket={1}>
      <div className="uitk-grid all-grid-align-center all-y-padding-four all-cell-1-1">
        <div className="uitk-cell all-cell-1-1 all-b-padding-four">
          <section className="uitk-grid all-grid-align-center">
            <meso-display-ad class="ad-div display-ad" uci="CM1" />
            <meso-media-query uci="CM2">
              <div>
                <meso-display-ad class="ad-div display-ad" uci="CM2" />
              </div>
            </meso-media-query>
            <meso-media-query uci="CM3">
              <div>
                <meso-display-ad class="ad-div display-ad" uci="CM3" />
              </div>
            </meso-media-query>
            <meso-display-ad class="ad-div display-ad" uci="CM6" />
          </section>
        </div>
        <Viewport>
          <ViewSmall>
            <>
              <MesoTrigger pageName={PageName.INFOSITE} />
              {isDateless && (
                <div className="uitk-cell all-cell-1-1">
                  <AdSenseComponent container={AdsenseContainer.HIS_FOOTER_1} searchQuery={searchQuery} width="100%" isMobile={true} />
                </div>
              )}
            </>
          </ViewSmall>
          <ViewMedium>
            <>
              {isDateless && <SwxlFooter context={context} className="uitk-cell all-cell-1-1 all-b-padding-four" />}
              <div className="uitk-cell all-cell-1-1">
                <AdSenseComponent container={AdsenseContainer.HIS_FOOTER_1} searchQuery={searchQuery} width="468px" />
              </div>
            </>
          </ViewMedium>
          <ViewLarge>
            <>
              {isDateless && <SwxlFooter context={context} className="uitk-cell all-r-padding-four uitk-flex-grow-1" />}
              <div className="uitk-cell uitk-flex-grow-1">
                <AdSenseComponent container={AdsenseContainer.HIS_FOOTER_2} searchQuery={searchQuery} width="100%" />
              </div>
            </>
          </ViewLarge>
        </Viewport>
      </div>
    </AbacusVariant>
  </AbacusExperiment>
);

interface AdSenseComponentProps {
  container: string;
  searchQuery: string;
  width: string;
  isMobile?: boolean;
}

const AdSenseComponent: React.FunctionComponent<AdSenseComponentProps> = props => {
  const { searchQuery, ...restOfProps } = props;

  return (
    <div className="uitk-grid all-grid-align-center">
      <MesoAdsenseCsa query={searchQuery}>
        <MesoCsaSlot type="content" {...restOfProps} />
      </MesoAdsenseCsa>
    </div>
  );
};

interface DisplayAdComponentProps {
  ads: DisplayAdModel[];
  pageName: string;
}

const DisplayAdComponent: React.FunctionComponent<DisplayAdComponentProps> = ({ ads, pageName }) => (
  <ErrorBoundary label="meso.footer">
    <section className="uitk-grid all-grid-align-center">
      {ads.map(({ mediaquery, uci }) => (
        <MesoDisplayAd key={uci} uci={uci} mediaquery={mediaquery} />
      ))}
    </section>
    <MesoTrigger pageName={pageName} />
  </ErrorBoundary>
);
